import React, { useState } from "react";
import {
  Table,
  Switch,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  message,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Header } from "../../../../components";
import {
  useFetchTelegramGroups,
  useCreateTelegramGroup,
  useUpdateTelegramGroup,
  useDeleteTelegramGroup,
} from "../hooks/useFetch";

function TelegramBotPage() {
  const companyId = sessionStorage.getItem("activeCompanyId");
  const { data: groups = [], isLoading, refetch } = useFetchTelegramGroups(companyId);
  const { mutateAsync: createGroup, isLoading: isCreating } = useCreateTelegramGroup();
  const { mutateAsync: updateGroup } = useUpdateTelegramGroup();
  const { mutateAsync: deleteGroup } = useDeleteTelegramGroup();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [togglingId, setTogglingId] = useState(null);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ chatId: record.chat_id, groupName: record.group_name || "" });
    setModalOpen(true);
  };

  const handleToggle = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await updateGroup({ id: record.id, enabled: checked });
      message.success(checked ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "เปลี่ยนสถานะไม่สำเร็จ");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      if (editing) {
        await updateGroup({ id: editing.id, chatId: v.chatId, groupName: v.groupName });
        message.success("บันทึกสำเร็จ");
      } else {
        await createGroup({ chatId: v.chatId, groupName: v.groupName, enabled: false });
        message.success("เพิ่มกลุ่มสำเร็จ (ยังปิดอยู่ — เปิดสวิตช์เมื่อพร้อม)");
      }
      setModalOpen(false);
      refetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteGroup(record.id);
      message.success("ลบแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "ลบไม่สำเร็จ");
    }
  };

  const columns = [
    {
      title: "เปิด/ปิด",
      dataIndex: "enabled",
      width: 90,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
    { title: "ชื่อกลุ่ม", dataIndex: "group_name", render: (n) => n || "-" },
    { title: "Chat ID", dataIndex: "chat_id", render: (c) => <Tag>{c}</Tag> },
    {
      title: "เพิ่มเมื่อ",
      dataIndex: "create_date",
      render: (t) => (t ? new Date(t).toLocaleString("th-TH") : "-"),
    },
    {
      title: "จัดการ",
      width: 130,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="ลบกลุ่มนี้?" onConfirm={() => handleDelete(record)} okText="ลบ" cancelText="ยกเลิก">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="m-4 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Header category="Admin" title="Telegram Bot — รับข้อความสร้างเคส" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          เพิ่มกลุ่ม
        </Button>
      </div>

      <p style={{ color: "#888", marginBottom: 16 }}>
        ผูกกลุ่ม Telegram เข้ากับบริษัทนี้ — ข้อความรูปแบบ <b>Name / User / amount</b> ในกลุ่มที่เปิดใช้งาน
        จะถูกสร้างเป็นเคส <b>Register</b> เข้าระบบอัตโนมัติ และบอทจะ reply กลับเมื่อเคสเข้าระบบ/สำเร็จ ·
        เอา Chat ID ได้จาก log ของบอทตอนมีข้อความเข้ากลุ่ม
      </p>

      <Table rowKey="id" loading={isLoading} dataSource={groups} columns={columns} pagination={false} />

      <Modal
        title={editing ? "แก้ไขกลุ่ม" : "เพิ่มกลุ่ม"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isCreating}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="chatId" label="Chat ID ของกลุ่ม" rules={[{ required: true, message: "กรอก Chat ID" }]}>
            <Input placeholder="เช่น -1001234567890" />
          </Form.Item>
          <Form.Item name="groupName" label="ชื่อกลุ่ม (ไว้จำ)">
            <Input placeholder="เช่น กลุ่มสมัคร MGBUSA" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TelegramBotPage;
